import { Link } from "@tanstack/react-router";

import { FaAmazon } from "react-icons/fa";
import { Button } from "../../components/core/basic/Button";
import { TextField } from "../../components/fields/base/TextField";
import { Flex } from "../../../styled-system/jsx";
import { Label } from "react-aria-components";
import { getAPIKeys } from "../../utils/getUrlPrefix";
import { apiClient } from "../../zodios/client";
import clearCookies from "../../utils/clearCookies";
import { useAppDispatch } from "../../redux/hooks";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { css, cx } from "../../../styled-system/css";
import { formField } from "../../../styled-system/recipes";
import { LWAButton, type LWAResult } from "../../components/custom/LoginWithAmazon";
import { AuthLayout } from "../../layouts/AuthLayout";
import { captureException } from "@sentry/react";
import { errorToast } from "../../utils/toasts";
import { tokenLogin } from "./tokens/tokenLogin";
import { useState } from "react";
import MFAForm from "./MultiFactorOTP";
import MFARecoveryForm from "./MFARecovery";
import { useTranslation } from "react-i18next";
import { isAxiosError, type AxiosError } from "axios";
const apiKeys = getAPIKeys();

const CLIENT_ID = apiKeys["API_AUTH_CLIENT_ID"];
const CLIENT_SECRET = apiKeys["API_AUTH_SECRET"];

type LoginView = "login" | "mfa" | "recovery";

interface LoginFailureData {
  error?: string;
  error_description?: string;
  detail?: string;
}

type LoginResult =
  | { status: "signedIn" }
  | { status: "mfaRequired" }
  | { status: "credentialsRejected"; error: AxiosError<LoginFailureData | undefined> };

const isRejectedCredentials = (error: unknown): error is AxiosError<LoginFailureData | undefined> =>
  isAxiosError<LoginFailureData | undefined>(error) &&
  error.response?.status === 400 &&
  error.response.data?.error === "invalid_grant";

interface LoginFormData {
  email: string;
  password: string;
  otp?: string;
  recovery_code?: string;
}

const INITIAL_DATA: LoginFormData = {
  email: "",
  password: "",
};

export const LoginForm = () => {
  const [view, setView] = useState<LoginView>("login");

  const { t } = useTranslation(["auth", "validation"]);
  const dispatch = useAppDispatch();
  const classes = formField();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
    trigger,
    getValues,
  } = useForm({
    mode: "onBlur",
    defaultValues: INITIAL_DATA,
  });

  const socialLoginMutation = useMutation({
    mutationFn: async (user: LWAResult) => {
      const userLoginInfo = {
        grant_type: "convert_token" as const,
        backend: "amazon",
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        token: user.accessToken,
      };

      const authRes = await apiClient.fetchLoginUserVerification({
        custom_api_request: "user_login_verification",
        user_email: user.email,
      });

      if (authRes === "User Available") {
        const tokenRes = await apiClient.convertTokens(userLoginInfo);
        await tokenLogin(tokenRes, dispatch);
      } else {
        sessionStorage.clear();
        localStorage.clear();
        clearCookies();
        if (authRes === "User Not Available") {
          setError("root", {
            type: "server",
            message: t(
              ($) =>
                $.auth.login[
                  "An error occurred attempting to sign in with Amazon. Did you mean to sign in with an email and password instead?"
                ],
            ),
          });
        } else {
          errorToast({
            title: t(($) => $.auth.login["Login Error"]),
            error: authRes,
          });
        }
      }
    },
    onError: (error) => {
      errorToast({
        title: t(($) => $.auth.login["Login Error"]),
        error,
      });
    },
  });

  const showLoginFailure = (error: { response?: { data?: LoginFailureData } }) => {
    const { error_description, detail } = error.response?.data ?? {};

    if (error_description) {
      setError("root", { type: "server", message: error_description });
    } else if (detail) {
      setError("root", { type: "server", message: detail });
    } else {
      errorToast({ title: t(($) => $.auth.login["Login Error"]), error });
    }
  };

  const loginFormMutation = useMutation({
    mutationFn: async (values: LoginFormData): Promise<LoginResult> => {
      const { email, password, otp, recovery_code } = values;

      const passwordLoginInfo = {
        grant_type: "password" as const,
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        username: email.trim(),
        password: password,
        otp: otp,
        recovery_code: recovery_code,
      };

      const tokenRes = await apiClient.login(passwordLoginInfo).catch((error: unknown) => {
        if (isRejectedCredentials(error)) {
          return error;
        }
        throw error;
      });

      if (isRejectedCredentials(tokenRes)) {
        return { status: "credentialsRejected", error: tokenRes };
      }

      if (tokenRes.requires_mfa) {
        return { status: "mfaRequired" };
      }

      await tokenLogin(tokenRes, dispatch);
      return { status: "signedIn" };
    },
    onSuccess: (result) => {
      if (result.status === "mfaRequired") {
        setView("mfa");
      } else if (result.status === "credentialsRejected") {
        showLoginFailure(result.error);
      }
    },
    onError: (error: { response?: { data?: LoginFailureData } }) => {
      showLoginFailure(error);
    },
  });

  return (
    <AuthLayout
      title={
        view === "recovery" ? t(($) => $.auth.recovery["Two-factor Recovery"])
        : view === "mfa" ?
          t(($) => $.auth.mfa["Two-factor Authentication"])
        : t(($) => $.auth.login["Welcome Back!"])
      }
      maxWidth={"360px"}
    >
      <>
        {view === "recovery" && (
          <MFARecoveryForm
            showLoginForm={() => {
              setView("login");
            }}
            onSuccess={async (recoveryCode) => {
              const valid = await trigger(["email", "password"]);
              if (!valid) return;

              const { email, password } = getValues();

              loginFormMutation.mutate({
                email,
                password,
                recovery_code: recoveryCode,
              });
            }}
            isPending={loginFormMutation.isPending}
            parentErrors={errors}
          />
        )}
        {view === "mfa" && (
          <MFAForm
            onSuccess={async (otp) => {
              const valid = await trigger(["email", "password"]);
              if (!valid) return;

              const { email, password } = getValues();
              loginFormMutation.mutate({ email, password, otp });
            }}
            parentErrors={errors}
            showRecoveryForm={() => setView("recovery")}
            isPending={loginFormMutation.isPending}
          />
        )}
        {view === "login" && (
          <>
            <form
              onSubmit={handleSubmit((data) => {
                loginFormMutation.mutate(data);
              })}
            >
              <Flex gap={"medium"} direction={"column"}>
                <Controller
                  name={"email"}
                  control={control}
                  rules={{
                    required: { value: true, message: t(($) => $.validation.email["An email is required"]) },
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t(($) => $.validation.email["Invalid email address"]),
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      label={t(($) => $.auth.login.Email)}
                      type={"email"}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      onBlur={field.onBlur}
                      errorMessage={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name={"password"}
                  control={control}
                  rules={{
                    required: { value: true, message: t(($) => $.validation.password["A password is required"]) },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      label={
                        <Flex justifyContent={"space-between"} width={"100%"}>
                          <Label>{t(($) => $.auth.login.Password)}</Label>{" "}
                          <Link to="/signin/resetpassword">{t(($) => $.auth.login["Forgot?"])}</Link>
                        </Flex>
                      }
                      type={"password"}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      onBlur={field.onBlur}
                      errorMessage={fieldState.error?.message}
                    />
                  )}
                />
                {typeof errors.root?.message === "string" && (
                  <p
                    className={cx(
                      classes.error,
                      css({
                        marginX: "auto",
                        textAlign: "center !important",
                      }),
                    )}
                  >
                    {errors.root.message}
                  </p>
                )}
              </Flex>
              <Button
                isPending={loginFormMutation.isPending || socialLoginMutation.isPending}
                className={css({ width: "100%", marginTop: "large" })}
                type="submit"
              >
                {t(($) => $.auth.login["Log In"])}
              </Button>
            </form>
            <hr className={css({ width: "100%", marginY: "medium" })} />
            <LWAButton
              isPending={loginFormMutation.isPending || socialLoginMutation.isPending}
              onSuccess={(user) => {
                socialLoginMutation.mutate(user);
              }}
              onError={(error) => {
                if (error instanceof Error && error.message === "access_denied") return;
                captureException(error);
              }}
            >
              {t(($) => $.auth.login["Continue with Amazon"])} <FaAmazon />
            </LWAButton>
            <div className={css({ width: "100%", marginTop: "medium", textAlign: "center" })}>
              {t(($) => $.auth.login["Want to get started today?"])}
              <br />
              <Link to="/signup">{t(($) => $.auth.login["Sign Up"])}</Link>
            </div>
          </>
        )}
      </>
    </AuthLayout>
  );
};
